import '@angular/core';

// Import base libraries (jQuery, Bootstrap, etc.)
import './config/base.library-imports';
import './config/core.library-imports';

import { NgModule, Injector } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { APP_BASE_HREF } from '@angular/common';
import { NvD3Module } from '../widgets/nvd3';
import 'd3';
import 'nvd3';

import { NgbModule, NgbDatepickerModule } from '@ng-bootstrap/ng-bootstrap';
import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';

// Angular 5 UI-Router
import { UIRouter, UIRouterModule } from '@uirouter/angular';

import { CoreModules } from './modules/core.modules';
import { AdminModule } from './modules/admin/admin.module';
import { CoreFiltersModule } from './core.filters.module';
import { CoreServicesModule } from './core.services.module';
import { ProjectModule } from './modules/project/project.module';
import { ProjectAdminModule } from './modules/project/admin/project.admin.module';
import { DocmanServicesModule } from '../plugins/docman/services/docman.services.module';
import { DocmanModule } from '../plugins/docman/docman.module';
import { TrackerServicesModule } from '../plugins/tracker/services/tracker.services.module';
import { TrackerLibModule } from '../plugins/tracker/lib/tracker.lib.module';
import { FrsModule } from '../plugins/frs/frs.module';
import { ForumModule } from '../plugins/forum/forum.module';
import { InterceptorConfigServiceAngular } from './config/core.interceptor.config.angular.service';
import { HttpUnauthorizedInterceptor } from './config/core.unauthorized.interceptor.angular';
import { CoreWidgetsModule } from '../widgets/core.widgets.module';
import { BuildModule } from '../plugins/build/build.module';
import { WikiModule } from '../plugins/wiki/wiki.module';
import { TrackerModule } from '../plugins/tracker/tracker.module';
import { ScmModule } from '../plugins/scm/scm.module';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ChatModule } from '../plugins/chat/gf-chat.module';
import { FlowModule } from '../plugins/flow/flow.module';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { FeatureFlagService } from './services/core.feature-flags.service';

import { CORE_STATES } from './config/core.routes.config';
import { PageContextService } from './services/core.page-context.service';
import { AccessDeniedService } from './services/core.access-denied.service';
import { AnalyticsService } from './services/core.analytics.service';


// Import Component decorator
import { Component } from '@angular/core';
import { AppComponent } from './modules/home/app.component';

export function createTranslateLoader(http: HttpClient) {
	return new TranslateHttpLoader(http, '/core/js/i18n/', '.json');
}

@Component({
    selector: "gf-app",
    template: `<ui-view></ui-view>`
})
export class GfApp {}

@NgModule({
    declarations: [
        GfApp,
        AppComponent
    ],
    imports: [
        BrowserModule,
        NgbModule,
        NgbDatepickerModule,
        NvD3Module,
        TypeaheadModule,
        CoreServicesModule,
        CoreFiltersModule,
        DocmanServicesModule,
        TrackerServicesModule,
        CoreModules,
        UIRouterModule.forRoot({
            states: CORE_STATES,
            useHash: false,
            otherwise: '/not-found',
            config: (uiRouter: any, injector: Injector) => {
                const pageContextService = injector.get(PageContextService);
                const analyticsService = injector.get(AnalyticsService);

                uiRouter.transitionService.onSuccess({}, (transition) => {
                    pageContextService.updateFromTransition(transition);
                    analyticsService.initialize();
                    analyticsService.trackPageView(window.location.pathname, pageContextService.getStructuredTitle());
                });
                // A resolve that rejects with a 401/403 is a navigation refusal, not a bug.
                // onError sees the Transition (defaultErrorHandler does not), and the
                // transition target is what the return URL has to be built from.
                uiRouter.transitionService.onError({}, (transition) => {
                    const accessDeniedService = injector.get(AccessDeniedService);
                    accessDeniedService.handleTransitionError(transition.error(), transition);
                });
                uiRouter.stateService.defaultErrorHandler((error) => {
                    if (error && (error.type === 2 || error.type === 3 || error.type === 5)) return;
                    const accessDeniedService = injector.get(AccessDeniedService);
                    if (accessDeniedService.wasHandled(error)) return;
                    console.error(error);
                });
            }
        }),
        ChatModule,
        FlowModule,
        HttpClientModule,
        FrsModule,
        CoreWidgetsModule,
        BuildModule,
        ProjectModule,
        ProjectAdminModule,
        TrackerLibModule,
        ForumModule,
        AdminModule,
        TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader,
				useFactory: createTranslateLoader,
				deps: [HttpClient]
			}
		}),
        WikiModule,
        TrackerModule,
        ScmModule,
        DocmanModule
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    providers: [
        { provide: HTTP_INTERCEPTORS, useClass: InterceptorConfigServiceAngular, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: HttpUnauthorizedInterceptor, multi: true },
        { provide: Window, useValue: window },
        { provide: APP_BASE_HREF, useValue: '/' },
        FeatureFlagService
    ],
    bootstrap: [GfApp]
})

export class AppModule {
    constructor() {
    }
}
